import React, { useState, useEffect, useRef } from 'react';
import { FolderOpen, Edit3, Settings, User, Grid3X3, Scan, Save, FilePlus, RotateCcw, Menu, Sliders, Camera } from 'lucide-react';

const PresentationToolbar = ({
  onLoadPresentation,
  onEditPresentation,
  onShowSettings,
  onShowAuth,
  onTogglePresentation,
  onCycleLayouter,
  onTriggerAR,
  onSave,
  onSaveAs,
  onSaveCurrentSettings,
  onSaveCameraPosition,
  contentPostId = null,
  onReload,
  currentTitle,
  onRenameTitle,
  currentLayouter = 'grid',
  isPresentationMode = false,
  isAuthenticated = false,
  user = null,
  isSaving = false,
  isDirty = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(currentTitle || 'Untitled');
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  // Platform detection for AR availability
  const isIOSSafari = () => {
    const ua = navigator.userAgent;
    return /iPad|iPhone|iPod/.test(ua) && /Safari/.test(ua) && !/Chrome/.test(ua);
  };
  const isAndroidMobile = () => /Android/.test(navigator.userAgent) && /Mobile/.test(navigator.userAgent);
  const isARSupported = isIOSSafari() || isAndroidMobile();

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Close dropdown on outside click or escape
  useEffect(() => {
    if (!isOpen) return;
    const onClickOutside = (e) => {
      if (menuRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('touchstart', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('touchstart', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  const triggerStyle = {
    position: 'fixed',
    top: isMobile ? '12px' : '16px',
    right: isMobile ? '12px' : '20px',
    zIndex: 1000,
    width: '40px',
    height: '40px',
    background: 'rgba(0, 0, 0, 0.35)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    borderRadius: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'rgba(255, 255, 255, 0.9)',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: isPresentationMode ? 0 : 1,
    pointerEvents: isPresentationMode ? 'none' : 'auto',
    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)'
  };

  const menuStyle = {
    position: 'fixed',
    top: isMobile ? '60px' : '64px',
    right: isMobile ? '12px' : '20px',
    zIndex: 1000,
    minWidth: '240px',
    maxWidth: 'calc(100vw - 24px)',
    background: 'rgba(20, 20, 24, 0.85)',
    backdropFilter: 'blur(24px) saturate(150%)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '14px',
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.5)',
    padding: '6px',
    opacity: isOpen ? 1 : 0,
    pointerEvents: isOpen ? 'auto' : 'none',
    transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.96)',
    transformOrigin: 'top right',
    transition: 'opacity 0.18s ease, transform 0.18s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  const titleHeaderStyle = {
    padding: '10px 12px 8px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    marginBottom: '4px',
    color: 'rgba(255, 255, 255, 0.95)',
    fontSize: '13px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };

  const itemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: isMobile ? '12px 12px' : '9px 12px',
    background: 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    color: 'rgba(255, 255, 255, 0.85)',
    fontSize: isMobile ? '15px' : '13px',
    fontFamily: 'inherit',
    textAlign: 'left',
    transition: 'background 0.12s ease, color 0.12s ease'
  };

  const handleAction = (fn) => {
    if (fn) fn();
    setIsOpen(false);
  };

  const handleTitleCommit = () => {
    setIsEditingTitle(false);
    if (onRenameTitle && titleDraft && titleDraft.trim() && titleDraft !== currentTitle) {
      onRenameTitle(titleDraft.trim());
    }
  };

  const titleInputStyle = {
    width: '100%',
    background: 'rgba(255, 255, 255, 0.08)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    borderRadius: '6px',
    padding: '6px 8px',
    fontSize: '16px',
    fontFamily: 'inherit'
  };

  const items = [
    { id: 'layouter', icon: Grid3X3, label: `Layout: ${currentLayouter}`, onClick: onCycleLayouter },
    { id: 'reload', icon: RotateCcw, label: 'Reload', onClick: onReload },
    { id: 'open', icon: FolderOpen, label: 'Open Presentation', onClick: onLoadPresentation },
    { id: 'save', icon: Save, label: isSaving ? 'Saving...' : (isDirty ? 'Save (unsaved)' : 'Save'), onClick: onSave, disabled: isSaving },
    // Save-current-settings disabled: existing MarkdownExporter is broken (writes [object Object]
    // for properties, drops all POI HTML-comment blocks). Re-enable once exporter is rebuilt.
    // { id: 'savesettings', icon: Sliders, label: 'Save Current Settings', onClick: onSaveCurrentSettings },
    // Camera-save only available when running a content post (?post=<id>)
    ...(contentPostId ? [{ id: 'savecam', icon: Camera, label: 'Save Camera Position', onClick: onSaveCameraPosition }] : []),
    { id: 'saveas', icon: FilePlus, label: 'Save As...', onClick: onSaveAs },
    { id: 'edit', icon: Edit3, label: 'Edit Markdown', onClick: onEditPresentation },
    ...(isARSupported ? [{ id: 'ar', icon: Scan, label: 'View in AR', onClick: onTriggerAR }] : []),
    { id: 'settings', icon: Settings, label: 'Settings', onClick: onShowSettings },
    { id: 'auth', icon: User, label: isAuthenticated ? (user?.displayName || user?.email || 'Account') : 'Sign In', onClick: onShowAuth }
  ];

  return (
    <>
      <button
        ref={triggerRef}
        style={triggerStyle}
        onClick={() => setIsOpen(o => !o)}
        aria-label="Menu"
        aria-expanded={isOpen}
      >
        <Menu size={18} strokeWidth={2} />
      </button>

      <div ref={menuRef} style={menuStyle} role="menu">
        <div style={titleHeaderStyle}>
          {isEditingTitle ? (
            <input
              style={titleInputStyle}
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={handleTitleCommit}
              onKeyDown={(e) => { if (e.key === 'Enter') handleTitleCommit(); if (e.key === 'Escape') { setIsEditingTitle(false); setTitleDraft(currentTitle || 'Untitled'); } }}
              autoFocus
            />
          ) : (
            <span
              title="Double-click to rename"
              onDoubleClick={() => { setTitleDraft(currentTitle || 'Untitled'); setIsEditingTitle(true); }}
            >
              {isDirty && <span style={{ color: '#ffcc00', marginRight: 4 }}>*</span>}
              {currentTitle || 'Untitled'}
            </span>
          )}
        </div>

        {items.map(({ id, icon: Icon, label, onClick, disabled }) => (
          <button
            key={id}
            role="menuitem"
            style={{ ...itemStyle, ...(disabled ? { opacity: 0.5, pointerEvents: 'none' } : {}) }}
            onClick={() => handleAction(onClick)}
            disabled={disabled}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)'; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)'; }}
          >
            {id === 'auth' && user?.photoURL ? (
              <img
                src={user.photoURL}
                alt=""
                data-html2canvas-ignore="true"
                style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
              />
            ) : (
              <Icon size={16} strokeWidth={2} style={{ flexShrink: 0, opacity: 0.7 }} />
            )}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
          </button>
        ))}
      </div>
    </>
  );
};

export default PresentationToolbar;
